/**
 * Navigation model — structure preserved from IMPLEMENTATION_MASTER_PLAN.md
 * §7.3 (three groups: Principal / Opérations / Configuration), by explicit
 * supervisor decision (2026-08-31): the visual identity is new, the
 * information architecture is not.
 *
 * Every item here is a real, working screen — the last four ("Clients",
 * "Marketing", "Paiements & COD", "Paramètres") joined at the "Élimination
 * BIENTÔT" lot (docs/resumée.txt), closing the platform's explicit rule
 * for this app: no nav item may point at an unbuilt screen. Each of the
 * four is grounded in real data (client/payment views are aggregated from
 * `GET /merchant/orders`, no new backend endpoint — see
 * `features/clients/api.ts` and `features/payments/api.ts`'s own headers);
 * where a sub-feature genuinely has no backend yet (campaigns, notification
 * preferences), the SCREEN still renders — it says so honestly in place
 * of the feature, never a blank route or a "Bientôt" badge in this sidebar.
 *
 * `NavItem` therefore carries no `status` field any more: there is no
 * "not yet built" state left to represent. Earlier entries, for reference:
 * Dashboard/Produits & stock/Inventaire (Produits & Stock lot); "Boutique"
 * (entrée 60, `GET`/`PUT /merchant/store`; moved into the Principal group,
 * right after Dashboard, at entrée 108 — onboarding order, not a group
 * reassignment: it stays the same screen and permission, only its position
 * changed); "Équipe & permissions" (entrée
 * 61, `/merchant/staff`, OQ-14); "Commandes" (entrée 70, `/merchant/orders`,
 * Sprint 12); "Livraison" (entrée 71, `/merchant/deliveries/pending`,
 * Sprint 14); "Analytics" (Analytics S17 lot, `GET /merchant/analytics`);
 * "Promotions" (lot 2 Promotions/Coupons, `/merchant/coupons`, OQ-25);
 * "Factures" (lot Facturation S15, `/merchant/invoices`); "Catégories"
 * (lot Catégories, `/merchant/categories`, backend Sprint 7/OQ-19);
 * "À traiter" (remplace l'ancienne page « Notifications », qui n'était que le
 * journal d'e-mails jamais délivrés : aucun fournisseur d'e-mail, OQ-12 —
 * l'écran dérive ses alertes des commandes, du stock, de la livraison et du
 * compte Jibex, sans rien stocker).
 *
 * Ordre validé (2026-10-07), du plus fréquent au plus rare : Principal = Tableau de bord, Boutique, À traiter,
 * Commandes, Produits & stock, Inventaire, Clients, Marketing, Analytics ; Opérations = Livraison, Paiements & COD,
 * Factures ; Configuration = Catégories, Promotions, puis les trois entrées de réglage qui existaient déjà
 * (Compte Jibex, Équipe & permissions, Paramètres), laissées en fin de groupe.
 *
 * Sous-menus (2026-10-29, à la demande du propriétaire, sur le modèle de Converty) : les trois groupes deviennent une
 * seule liste où les écrans d'un même domaine se replient sous une entrée (Commandes, Produits, Livraison, Paiements,
 * Paramètres). Aucun écran n'est ajouté ni retiré ; seule la présentation change.
 */
import { type MessageKey } from '@ecom/i18n';

export interface NavItem {
  readonly labelKey: MessageKey;
  readonly to: string;
}

/** An entry that folds several screens of one domain (« Produits ▸ Produits & stock, Inventaire… »). */
export interface NavSubmenu {
  readonly labelKey: MessageKey;
  /** Identifies the entry (its icon, its open/closed state); the route of its first screen. */
  readonly id: string;
  readonly children: readonly NavItem[];
}

export type NavEntry = NavItem | NavSubmenu;

export function isSubmenu(entry: NavEntry): entry is NavSubmenu {
  return 'children' in entry;
}

export const NAV_MENU: readonly NavEntry[] = [
  { labelKey: 'merchant.nav.dashboard', to: '/dashboard' },
  { labelKey: 'merchant.nav.todo', to: '/todo' },
  {
    labelKey: 'merchant.nav.orders',
    id: '/orders',
    children: [
      { labelKey: 'merchant.nav.orders', to: '/orders' },
      { labelKey: 'merchant.nav.sales', to: '/sales' },
    ],
  },
  {
    labelKey: 'merchant.nav.menu.products',
    id: '/products',
    children: [
      { labelKey: 'merchant.nav.products', to: '/products' },
      { labelKey: 'merchant.nav.inventory', to: '/inventory' },
      { labelKey: 'merchant.nav.categories', to: '/categories' },
      { labelKey: 'merchant.nav.promotions', to: '/promotions' },
    ],
  },
  { labelKey: 'merchant.nav.customers', to: '/customers' },
  {
    labelKey: 'merchant.nav.delivery',
    id: '/delivery',
    children: [
      { labelKey: 'merchant.ship.title', to: '/delivery' },
      { labelKey: 'merchant.nav.deliveryAccount', to: '/delivery/account' },
    ],
  },
  {
    labelKey: 'merchant.nav.menu.payments',
    id: '/payments',
    children: [
      { labelKey: 'merchant.nav.payments', to: '/payments' },
      { labelKey: 'merchant.nav.invoices', to: '/invoices' },
    ],
  },
  { labelKey: 'merchant.nav.marketing', to: '/marketing' },
  { labelKey: 'merchant.nav.analytics', to: '/analytics' },
  { labelKey: 'merchant.nav.store', to: '/store' },
  { labelKey: 'merchant.nav.team', to: '/team' },
  {
    labelKey: 'merchant.nav.settings',
    id: '/settings',
    children: [
      { labelKey: 'merchant.nav.settings', to: '/settings' },
      // Everyone's own security history (lot B3).
      { labelKey: 'account.nav', to: '/account' },
    ],
  },
];

/**
 * The sidebar's section titles, keyed by the entry each section starts at (the menu order itself is unchanged):
 * Général (dashboard, to do), Ventes (orders … payments), Croissance (marketing, analytics), Gestion (shop … settings).
 */
export const NAV_SECTION_STARTS: Readonly<Record<string, MessageKey>> = {
  '/dashboard': 'merchant.nav.section.general',
  '/orders': 'merchant.nav.section.sales',
  '/marketing': 'merchant.nav.section.growth',
  '/store': 'merchant.nav.section.manage',
};

/** A screen, with the menu entry it folds under (`null` at the top level). */
export interface NavScreen extends NavItem {
  readonly parentKey: MessageKey | null;
}

/** Every screen once, in menu order (the command palette, the tests). */
export const NAV_ITEMS: readonly NavScreen[] = NAV_MENU.flatMap((entry): NavScreen[] =>
  isSubmenu(entry)
    ? entry.children.map((child) => ({ ...child, parentKey: entry.labelKey }))
    : [{ ...entry, parentKey: null }],
);

/** The submenu holding a route, if any (the sidebar opens it for the current page). */
export function submenuOf(pathname: string): NavSubmenu | undefined {
  return NAV_MENU.filter(isSubmenu).find((entry) =>
    entry.children.some((child) => child.to === pathname),
  );
}
